import {
  Box,
  Chip,
  FormControlLabel,
  List,
  ListItem,
  Switch,
  Table,
  TableBody,
  TableHead,
  TableRow,
  Tab,
  Tabs,
  Typography,
} from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { createFileRoute, redirect, useNavigate } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";

import {
  AudienceTableContainer,
  HeaderCell,
  RowCell,
} from "@/features/Audiences/AudienceTable";
import { alternatingRowBg } from "@/features/Audiences/tableUtils";
import { SettingsPageLayout } from "@/features/Settings/SettingsPageLayout";
import {
  DYNAMIC_FLAGS,
  isDynamicFeatureFlagsEnabled,
  useFeatureFlagOverrideStore,
} from "@/utils/featureFlagOverride";
import {
  isPrivilegedModeToggleAvailable,
  useHasElevatedAccess,
} from "@/utils/privilegedMode";

function getFeatureFlags(): [string, string][] {
  return Object.entries(import.meta.env)
    .filter(
      (entry): entry is [string, string] =>
        entry[0].startsWith("VITE_FF_") && typeof entry[1] === "string",
    )
    .sort(([a], [b]) => a.localeCompare(b));
}

function isBooleanFlagValue(value: string): value is "true" | "false" {
  return value === "true" || value === "false";
}

export const Route = createFileRoute("/_auth/settings/$siteId/feature-flags")({
  beforeLoad: () => {
    if (!isPrivilegedModeToggleAvailable && !isDynamicFeatureFlagsEnabled) {
      throw redirect({ to: ".." });
    }
  },
  component: FeatureFlagsPage,
});

function FeatureFlagValue({ value }: { value: string }) {
  if (isBooleanFlagValue(value)) {
    return (
      <Switch
        checked={value === "true"}
        disabled
        size="small"
        inputProps={{ "aria-label": `Feature flag value: ${value}` }}
      />
    );
  }

  if (value === "") {
    return (
      <Typography
        variant="body2"
        color="text.secondary"
        sx={{ fontStyle: "italic" }}
      >
        (empty)
      </Typography>
    );
  }

  return (
    <Typography
      variant="body2"
      sx={{ fontFamily: "monospace", wordBreak: "break-all" }}
    >
      {value}
    </Typography>
  );
}

function StaticFlagsPanel() {
  const flags = useMemo(() => getFeatureFlags(), []);

  return (
    <>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 4 }}>
        Current values for all VITE_FF_* environment variables loaded at build
        time. Change them in your local .env and restart the dev server to
        update.
      </Typography>
      {flags.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          No VITE_FF_* feature flags found.
        </Typography>
      ) : (
        <AudienceTableContainer>
          <Table size="small" aria-label="Feature flags">
            <TableHead>
              <TableRow>
                <HeaderCell sx={{ width: "55%" }}>Flag</HeaderCell>
                <HeaderCell sx={{ width: "45%" }}>Value</HeaderCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {flags.map(([name, value], index) => (
                <TableRow
                  key={name}
                  hover
                  sx={{ backgroundColor: alternatingRowBg(index) }}
                >
                  <RowCell
                    sx={{
                      fontFamily: "monospace",
                      fontSize: "0.8125rem",
                      verticalAlign: "middle",
                    }}
                  >
                    {name}
                  </RowCell>
                  <RowCell sx={{ verticalAlign: "middle" }}>
                    <FeatureFlagValue value={value} />
                  </RowCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </AudienceTableContainer>
      )}
    </>
  );
}

function DynamicFlagsPanel() {
  const queryClient = useQueryClient();
  const overrides = useFeatureFlagOverrideStore((s) => s.overrides);
  const setOverride = useFeatureFlagOverrideStore((s) => s.setOverride);

  function handleToggle(key: string, enabled: boolean) {
    setOverride(key, enabled);
    void queryClient.invalidateQueries();
  }

  return (
    <>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 4 }}>
        Override feature flags at runtime for your session only. Changes take
        effect immediately and persist across page reloads. A flag is on when
        toggled on here or enabled in the build env var.
      </Typography>
      <List disablePadding>
        {DYNAMIC_FLAGS.map((flag) => {
          const enabled = overrides[flag.key] ?? flag.envValue;
          return (
            <ListItem
              key={flag.key}
              disablePadding
              sx={{ py: 2, borderBottom: 1, borderColor: "divider" }}
            >
              <FormControlLabel
                sx={{ m: 0, alignItems: "flex-start" }}
                control={
                  <Switch
                    checked={enabled}
                    onChange={(_, checked) => handleToggle(flag.key, checked)}
                    inputProps={{ "aria-label": flag.label }}
                  />
                }
                label={
                  <Box>
                    <Typography variant="body1">{flag.label}</Typography>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ display: "block", fontFamily: "monospace" }}
                    >
                      {flag.key}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {flag.description}
                    </Typography>
                  </Box>
                }
              />
            </ListItem>
          );
        })}
      </List>
    </>
  );
}

function FeatureFlagsPage() {
  const navigate = useNavigate();
  const hasElevatedAccess = useHasElevatedAccess();

  const tabs = useMemo(() => {
    const result: { key: "all" | "dynamic"; label: string }[] = [];
    if (isPrivilegedModeToggleAvailable) {
      result.push({ key: "all", label: "All Flags" });
    }
    if (isDynamicFeatureFlagsEnabled && hasElevatedAccess) {
      result.push({ key: "dynamic", label: "Dynamic" });
    }
    return result;
  }, [hasElevatedAccess]);

  const [tabIndex, setTabIndex] = useState(0);

  useEffect(() => {
    if (tabs.length === 0) {
      void navigate({ to: ".." });
    }
  }, [tabs.length, navigate]);

  if (tabs.length === 0) return null;

  const activeTab = tabs[Math.min(tabIndex, tabs.length - 1)];

  return (
    <SettingsPageLayout
      breadcrumbs={[{ label: "Settings" }]}
      title="Feature Flags"
      titleAdornment={<Chip label="Internal" size="small" color="warning" />}
      data-testid="settings-feature-flags-page"
    >
      {tabs.length > 1 && (
        <Box sx={{ borderBottom: 1, borderColor: "divider", mb: 3 }}>
          <Tabs
            value={tabIndex}
            onChange={(_, value: number) => setTabIndex(value)}
            aria-label="Feature flags tabs"
          >
            {tabs.map((tab) => (
              <Tab key={tab.key} label={tab.label} />
            ))}
          </Tabs>
        </Box>
      )}
      {activeTab.key === "dynamic" ? (
        <DynamicFlagsPanel />
      ) : (
        <StaticFlagsPanel />
      )}
    </SettingsPageLayout>
  );
}
